// Draft Service
// Handles saving and loading chat drafts with complete context using MongoDB API

import axios from 'axios';

// API base URL
const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api';

// Create axios instance for draft API
const draftApi = axios.create({
  baseURL: `${API_BASE_URL}/drafts`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Interface for a chat message
export interface DraftMessage {
  id: number;
  text: string;
  isUser: boolean;
  agentType?: string;
  documentTitle?: string;
  documentContent?: string;
  tableOfContents?: string;
}

// Interface for agent configuration
export interface DraftAgent {
  id: string;
  name: string;
  siteUrl: string | string[];
  description?: string;
}

// Interface for a complete draft
export interface ChatDraft {
  id: string;
  title: string;
  description?: string;
  messages: DraftMessage[];
  documentContent: string;
  markdownContent: string;
  documentTitle: string;
  tableOfContents: string;
  selectedAgent?: DraftAgent;
  createdAt: string;
  updatedAt: string;
  lastActivity: string;
}

// Interface for draft summary (for listing)
export interface DraftSummary {
  id: string;
  title: string;
  description?: string;
  documentTitle: string;
  messageCount: number;
  lastActivity: string;
  createdAt: string;
  updatedAt: string;
  preview: string; // First few characters of the last message or document
}

/**
 * Save a new draft or update an existing one
 */
export const saveDraft = async (
  title: string,
  messages: DraftMessage[],
  documentContent: string,
  markdownContent: string,
  documentTitle: string,
  tableOfContents: string,
  selectedAgent?: DraftAgent,
  description?: string,
  existingDraftId?: string
): Promise<string> => {
  try {
    const draftData = {
      title,
      description,
      messages,
      documentContent,
      markdownContent,
      documentTitle,
      tableOfContents,
      selectedAgent,
    };

    let response;

    if (existingDraftId) {
      // Update existing draft
      response = await draftApi.put(`/${existingDraftId}`, draftData);
    } else {
      // Create new draft
      response = await draftApi.post('/', draftData);
    }

    const responseData = response.data as any;
    console.log('Draft saved successfully:', {
      id: responseData.id,
      title: responseData.title,
      messageCount: responseData.messageCount,
    });

    return responseData.id;
  } catch (error: any) {
    console.error('Error saving draft:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to save draft');
  }
};

/**
 * Load a specific draft by ID
 */
export const loadDraft = async (draftId: string): Promise<ChatDraft> => {
  try {
    const response = await draftApi.get<ChatDraft>(`/${draftId}`);
    const responseData = response.data as any;

    console.log('Draft loaded successfully:', {
      id: responseData.id,
      title: responseData.title,
      messageCount: responseData.messages?.length || 0,
      documentTitle: responseData.documentTitle,
    });

    return responseData;
  } catch (error: any) {
    console.error('Error loading draft:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to load draft');
  }
};

/**
 * Get all draft summaries for listing
 */
export const getDraftSummaries = async (): Promise<DraftSummary[]> => {
  try {
    const response = await draftApi.get('/');
    const responseData = response.data as any;
    return responseData.drafts || [];
  } catch (error: any) {
    console.error('Error getting draft summaries:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to get draft summaries');
  }
};

/**
 * Delete a draft by ID
 */
export const deleteDraft = async (draftId: string): Promise<void> => {
  try {
    await draftApi.delete(`/${draftId}`);
    console.log('Draft deleted successfully:', draftId);
  } catch (error: any) {
    console.error('Error deleting draft:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete draft');
  }
};

/**
 * Duplicate a draft (create a copy)
 */
export const duplicateDraft = async (draftId: string): Promise<string> => {
  try {
    const response = await draftApi.post(`/${draftId}/duplicate`);
    const responseData = response.data as any;

    console.log('Draft duplicated successfully:', {
      originalId: draftId,
      newId: responseData.id,
      newTitle: responseData.title,
    });

    return responseData.id;
  } catch (error: any) {
    console.error('Error duplicating draft:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to duplicate draft');
  }
};

/**
 * Update draft metadata (title, description) without changing content
 */
export const updateDraftMetadata = async (
  draftId: string,
  title: string,
  description?: string
): Promise<void> => {
  try {
    await draftApi.patch(`/${draftId}/metadata`, {
      title,
      description,
    });

    console.log('Draft metadata updated successfully:', draftId);
  } catch (error: any) {
    console.error('Error updating draft metadata:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update draft metadata');
  }
};

/**
 * Clear all drafts (with confirmation)
 */
export const clearAllDrafts = async (): Promise<void> => {
  try {
    await draftApi.delete('/', {
      headers: {
        'X-Confirm-Delete-All': 'true',
      },
    });

    console.log('All drafts cleared successfully');
  } catch (error: any) {
    console.error('Error clearing all drafts:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to clear all drafts');
  }
};

/**
 * Get storage statistics
 */
export const getDraftStats = async (): Promise<{
  totalDrafts: number;
  totalMessages: number;
  storageSize: number;
  oldestDraft?: string;
  newestDraft?: string;
}> => {
  try {
    const response = await draftApi.get('/stats');
    return response.data as any;
  } catch (error: any) {
    console.error('Error getting draft stats:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to get draft stats');
  }
};

/**
 * Search drafts by query
 */
export const searchDrafts = async (
  query: string,
  limit: number = 10
): Promise<DraftSummary[]> => {
  try {
    if (!query || query.trim().length < 2) {
      throw new Error('Search query must be at least 2 characters');
    }

    const response = await draftApi.get('/', {
      params: {
        search: query.trim(),
        limit,
      },
    });

    const responseData = response.data as any;
    return responseData.drafts || [];
  } catch (error: any) {
    console.error('Error searching drafts:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to search drafts');
  }
};

/**
 * Get drafts with pagination
 */
export const getDraftsPaginated = async (
  page: number = 1,
  limit: number = 20,
  sortBy: string = 'updatedAt',
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<{
  drafts: DraftSummary[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}> => {
  try {
    const response = await draftApi.get('/', {
      params: {
        page,
        limit,
        sortBy,
        sortOrder,
      },
    });

    return response.data as any;
  } catch (error: any) {
    console.error('Error getting paginated drafts:', error);
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to get paginated drafts');
  }
};
